import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, CheckCircle2, Archive, GitBranch, Loader2 } from "lucide-react";
import {
  SafetyTemplate, STATUS_LABEL, STATUS_VARIANT, TYPE_LABEL, TARGET_GROUP_LABEL,
} from "./types";
import { SafetyTemplateEditor } from "./SafetyTemplateEditor";
import { SafetyCategoriesManager } from "./SafetyCategoriesManager";
import { SafetyQuestionsManager } from "./SafetyQuestionsManager";

interface Props {
  template: SafetyTemplate;
  isAdmin: boolean;
  onReload: () => void;
  onTemplateChange: (t: SafetyTemplate) => void;
}

export const SafetyTemplateDetail = ({ template, isAdmin, onReload, onTemplateChange }: Props) => {
  const { user } = useAuth();
  const [editorOpen, setEditorOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await supabase
      .from("safety_instruction_templates")
      .select("*")
      .eq("id", template.id)
      .maybeSingle();
    if (data) onTemplateChange(data as unknown as SafetyTemplate);
    onReload();
  };

  const handleApprove = async () => {
    if (!isAdmin || !user) return;
    setBusy(true);
    const { error } = await supabase
      .from("safety_instruction_templates")
      .update({
        status: "active",
        approved_by: user.id,
        approved_at: new Date().toISOString(),
      })
      .eq("id", template.id);
    setBusy(false);
    if (error) toast.error(error.message);
    else { toast.success("Vorlage freigegeben und aktiviert"); refresh(); }
  };

  const handleArchive = async () => {
    if (!isAdmin) return;
    setBusy(true);
    const { error } = await supabase
      .from("safety_instruction_templates")
      .update({ status: "archived", archived_at: new Date().toISOString(), is_active: false })
      .eq("id", template.id);
    setBusy(false);
    if (error) toast.error(error.message);
    else { toast.success("Vorlage archiviert"); refresh(); }
  };

  const handleNewVersion = async () => {
    if (!user) return;
    setBusy(true);
    try {
      // Clone template
      const { data: newTpl, error: e1 } = await supabase
        .from("safety_instruction_templates")
        .insert({
          title: template.title,
          description: template.description,
          instruction_type: template.instruction_type,
          target_group: template.target_group,
          legal_basis: template.legal_basis,
          repeat_interval_months: template.repeat_interval_months,
          passing_score_percent: template.passing_score_percent,
          max_attempts: template.max_attempts,
          requires_followup_resolution: template.requires_followup_resolution,
          responsible_admin_id: template.responsible_admin_id,
          version: template.version + 1,
          parent_template_id: template.id,
          status: "draft",
          created_by: user.id,
        })
        .select()
        .single();
      if (e1) throw e1;

      // Clone categories & items & questions
      const { data: cats } = await supabase
        .from("safety_instruction_categories")
        .select("*")
        .eq("template_id", template.id);
      for (const c of cats || []) {
        const { data: newCat, error: ec } = await supabase
          .from("safety_instruction_categories")
          .insert({
            template_id: newTpl.id,
            title: c.title, description: c.description, sort_order: c.sort_order,
            is_required: c.is_required, is_active: c.is_active, is_library: false,
          }).select().single();
        if (ec || !newCat) continue;
        const { data: items } = await supabase
          .from("safety_instruction_items")
          .select("*").eq("category_id", c.id);
        for (const it of items || []) {
          const { data: newItem } = await supabase
            .from("safety_instruction_items")
            .insert({
              category_id: newCat.id,
              title: it.title, content: it.content, risk_description: it.risk_description,
              protective_measures: it.protective_measures, employee_behavior: it.employee_behavior,
              employee_confirmation_text: it.employee_confirmation_text,
              minimum_read_time_seconds: it.minimum_read_time_seconds,
              image_url: it.image_url, attachment_url: it.attachment_url,
              external_link: it.external_link, sort_order: it.sort_order,
              is_required: it.is_required, is_active: it.is_active,
            }).select().single();
          if (!newItem) continue;
          const { data: qs } = await supabase
            .from("safety_instruction_questions")
            .select("*").eq("item_id", it.id);
          for (const q of qs || []) {
            await supabase.from("safety_instruction_questions").insert({
              item_id: newItem.id, category_id: newCat.id,
              question: q.question, question_type: q.question_type,
              answer_options: q.answer_options, correct_answer: q.correct_answer,
              explanation: q.explanation, requires_manual_review: q.requires_manual_review,
              is_required: q.is_required, is_scored: q.is_scored,
              difficulty: q.difficulty, sort_order: q.sort_order,
            });
          }
        }
      }
      toast.success("Neue Version erstellt");
      onTemplateChange(newTpl as unknown as SafetyTemplate);
      onReload();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Fehler beim Erstellen der neuen Version");
    } finally {
      setBusy(false);
    }
  };

  const isLocked = template.status === "approved" || template.status === "active" || template.status === "archived";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 flex-wrap">
                {template.title}
                <Badge variant={STATUS_VARIANT[template.status]}>{STATUS_LABEL[template.status]}</Badge>
                <Badge variant="outline">v{template.version}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {TYPE_LABEL[template.instruction_type]} · {TARGET_GROUP_LABEL[template.target_group]} · Wiederholung: {template.repeat_interval_months} Monate · Bestehen: {template.passing_score_percent}%
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditorOpen(true)} className="gap-2">
                <Pencil className="h-4 w-4" /> Metadaten
              </Button>
              {isLocked && isAdmin && (
                <Button variant="outline" onClick={handleNewVersion} disabled={busy} className="gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />}
                  Neue Version
                </Button>
              )}
              {!isLocked && isAdmin && (
                <Button onClick={handleApprove} disabled={busy} className="gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Freigeben &amp; Aktivieren
                </Button>
              )}
              {template.status !== "archived" && isAdmin && (
                <Button variant="outline" onClick={handleArchive} disabled={busy} className="gap-2">
                  <Archive className="h-4 w-4" /> Archivieren
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {template.description && (
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{template.description}</p>
            {template.legal_basis && template.legal_basis.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {template.legal_basis.map((l, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{l}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories">Kategorien &amp; Punkte</TabsTrigger>
          <TabsTrigger value="questions">Fragenkatalog</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-4">
          <SafetyCategoriesManager templateId={template.id} locked={isLocked} />
        </TabsContent>
        <TabsContent value="questions" className="mt-4">
          <SafetyQuestionsManager templateId={template.id} locked={isLocked} />
        </TabsContent>
      </Tabs>

      <SafetyTemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={template}
        onSaved={() => { setEditorOpen(false); refresh(); }}
      />
    </div>
  );
};
